import {
  Box,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, useFormContext } from "react-hook-form";
import { CreatePatientForm } from "types";
import {
  genderOptions,
  maritalStatusOptions,
} from "validations/patientSchemas";

import ImageUpload from "./ImageUpload";

function PersonalInformationSection() {
  const {
    control,
    formState: { errors },
  } = useFormContext<CreatePatientForm>();

  return (
    <Box className="space-y-6">
      <ImageUpload name="profileImage" />
      <Typography variant="h6" className="text-xl font-medium">
        Personal Information
      </Typography>
      <Box className="grid grid-cols-3 gap-4">
        {/* First Row */}
        <Controller
          name="firstName"
          control={control}
          render={({ field }) => (
            <TextField
              id="first-name"
              label="First Name"
              variant="outlined"
              fullWidth
              error={!!errors.firstName}
              helperText={errors.firstName?.message}
              {...field}
            />
          )}
        />

        <Controller
          name="middleName"
          control={control}
          render={({ field }) => (
            <TextField
              id="middle-name"
              label="Middle Name (optional)"
              variant="outlined"
              fullWidth
              error={!!errors.middleName}
              helperText={errors.middleName?.message}
              {...field}
            />
          )}
        />

        <Controller
          name="lastName"
          control={control}
          render={({ field }) => (
            <TextField
              id="last-name"
              label="Last Name"
              variant="outlined"
              fullWidth
              error={!!errors.lastName}
              helperText={errors.lastName?.message}
              {...field}
            />
          )}
        />

        {/* Second Row */}
        <Controller
          name="dateOfBirth"
          control={control}
          render={({ field }) => (
            <TextField
              id="date-of-birth"
              label="Date of Birth"
              variant="outlined"
              type="date"
              fullWidth
              error={!!errors.dateOfBirth}
              helperText={errors.dateOfBirth?.message as string}
              InputLabelProps={{ shrink: true }}
              {...field}
              onChange={(e) => {
                const date = e.target.value ? new Date(e.target.value) : null;
                field.onChange(date);
              }}
              value={
                field.value
                  ? new Date(field.value).toISOString().split("T")[0]
                  : ""
              }
            />
          )}
        />

        <Controller
          name="gender"
          control={control}
          render={({ field }) => (
            <FormControl fullWidth error={!!errors.gender}>
              <InputLabel id="gender-label">Gender</InputLabel>
              <Select
                labelId="gender-label"
                id="gender"
                label="Gender"
                {...field}
                value={field.value || ""}
              >
                {genderOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
              {errors.gender && (
                <FormHelperText>
                  {errors.gender.message as string}
                </FormHelperText>
              )}
            </FormControl>
          )}
        />

        <Controller
          name="maritalStatus"
          control={control}
          render={({ field }) => (
            <FormControl fullWidth error={!!errors.maritalStatus}>
              <InputLabel id="marital-status-label">Marital Status</InputLabel>
              <Select
                labelId="marital-status-label"
                id="marital-status"
                label="Marital Status"
                {...field}
                value={field.value || ""}
              >
                {maritalStatusOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
              {errors.maritalStatus && (
                <FormHelperText>
                  {errors.maritalStatus.message as string}
                </FormHelperText>
              )}
            </FormControl>
          )}
        />

        {/* Third Row - Address spans all 3 columns */}
        <Controller
          name="address"
          control={control}
          render={({ field }) => (
            <TextField
              id="address"
              label="Address"
              variant="outlined"
              fullWidth
              error={!!errors.address}
              helperText={errors.address?.message as string}
              className="col-span-3"
              multiline
              {...field}
            />
          )}
        />
      </Box>
    </Box>
  );
}

export default PersonalInformationSection;
